import type { FieldAppSettings } from '@/app/settings/app-settings';
import { readInventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import {
  FIELD_SYNC_TRANSPORT_STATUSES,
  type FieldSyncTransport,
  FieldSyncTransportError,
  type FieldSyncTransportResult,
} from '@/features/sync/engine/field-sync-transport';
import { HttpFieldSyncTransport } from '@/features/sync/engine/http-field-sync-transport';
import { mapInventorizationSessionToSyncPayload } from '@/features/sync/engine/inventorization-sync-payload';
import { CAPTURE_SESSION_STATUSES, type CaptureSession, WORKFLOW_TYPES } from '@/shared/capture/capture-session';
import { fieldDatabase } from '@/shared/db/default-field-database';
import { deleteLocalPhotoBlobs } from '@/shared/db/photo-blob-cleanup';
import { CaptureSessionRepository } from '@/shared/db/repositories/capture-session-repository';
import { FloorplanBlobRepository } from '@/shared/db/repositories/floorplan-blob-repository';
import { PhotoBlobRepository } from '@/shared/db/repositories/photo-blob-repository';
import { SyncQueueRepository } from '@/shared/db/repositories/sync-queue-repository';
import {
  type FieldDatabase,
  SYNC_QUEUE_STATUSES,
  type SyncQueueRecord,
  type SyncQueueStatus,
} from '@/shared/db/schema';

export const FIELD_SYNC_RUN_STATUSES = {
  failed: 'failed',
  idle: 'idle',
} as const;

export type FieldSyncRunStatus =
  | (typeof FIELD_SYNC_RUN_STATUSES)[keyof typeof FIELD_SYNC_RUN_STATUSES]
  | Extract<SyncQueueStatus, 'synced' | 'conflict' | 'needs_review'>;

export interface FieldSyncRunResult {
  errorCode?: string;
  sessionId?: string;
  status: FieldSyncRunStatus;
}

interface FieldSyncRunnerRepositories {
  captureSessions: CaptureSessionRepository;
  floorplanBlobs: FloorplanBlobRepository;
  photoBlobs: PhotoBlobRepository;
  syncQueue: SyncQueueRepository;
}

export function createFieldSyncRunner(
  settings: FieldAppSettings,
  input: {
    database?: FieldDatabase;
    floorplanBlobs?: ConstructorParameters<typeof HttpFieldSyncTransport>[0]['floorplanBlobs'];
    photoBlobs?: ConstructorParameters<typeof HttpFieldSyncTransport>[0]['photoBlobs'];
  } = {},
) {
  return new FieldSyncRunner(
    input.database ?? fieldDatabase,
    new HttpFieldSyncTransport({
      baseUrl: settings.apiBaseUrl,
      floorplanBlobs: input.floorplanBlobs,
      photoBlobs: input.photoBlobs,
    }),
  );
}

export class FieldSyncRunner {
  private readonly repositories: FieldSyncRunnerRepositories;

  constructor(
    database: FieldDatabase,
    private readonly transport: FieldSyncTransport,
  ) {
    this.repositories = {
      captureSessions: new CaptureSessionRepository(database),
      floorplanBlobs: new FloorplanBlobRepository(database),
      photoBlobs: new PhotoBlobRepository(database),
      syncQueue: new SyncQueueRepository(database),
    };
  }

  async runNext(now = new Date().toISOString(), input: { sessionIds?: Set<string> } = {}): Promise<FieldSyncRunResult> {
    await this.repositories.syncQueue.recoverStaleSyncing(now, { sessionIds: input.sessionIds });

    const [record] = await this.repositories.syncQueue.listPending({ sessionIds: input.sessionIds });

    if (!record) {
      return { status: FIELD_SYNC_RUN_STATUSES.idle };
    }

    return this.runRecord(record, now);
  }

  async runAll(now = new Date().toISOString(), input: { sessionIds?: Set<string> } = {}) {
    const results: FieldSyncRunResult[] = [];

    while (true) {
      const result = await this.runNext(now, { sessionIds: input.sessionIds });

      if (result.status === FIELD_SYNC_RUN_STATUSES.idle) {
        return results;
      }

      results.push(result);
    }
  }

  private async runRecord(record: SyncQueueRecord, now: string): Promise<FieldSyncRunResult> {
    await this.repositories.syncQueue.markSyncing(record.sessionId, now);

    const session = await this.repositories.captureSessions.get(record.sessionId);
    const preflightErrorCode = readFieldSyncPreflightError(record, session);

    if (preflightErrorCode || !session) {
      const errorCode = preflightErrorCode ?? 'SESSION_NOT_FOUND';
      await recordFieldSyncFailure(this.repositories, record.sessionId, now, errorCode);

      return {
        errorCode,
        sessionId: record.sessionId,
        status: FIELD_SYNC_RUN_STATUSES.failed,
      };
    }

    const syncSession = {
      ...session,
      locationApiKey: record.locationApiKey,
    };

    try {
      const payload = mapInventorizationSessionToSyncPayload(syncSession, {
        idempotencyKey: record.idempotencyKey,
      });
      const transportResult = await this.transport.submitInventorization(payload);

      return applyFieldSyncTransportResult(this.repositories, syncSession, transportResult, now);
    } catch (error) {
      const errorCode = error instanceof FieldSyncTransportError ? error.errorCode : 'NETWORK_OR_TRANSPORT_ERROR';
      await recordFieldSyncFailure(this.repositories, record.sessionId, now, errorCode);

      return {
        errorCode,
        sessionId: record.sessionId,
        status: FIELD_SYNC_RUN_STATUSES.failed,
      };
    }
  }
}

function readFieldSyncPreflightError(record: SyncQueueRecord, session: CaptureSession | undefined) {
  if (!session) {
    return 'SESSION_NOT_FOUND';
  }

  if (session.workflowType !== WORKFLOW_TYPES.inventorize) {
    return 'WORKFLOW_NOT_SUPPORTED';
  }

  if (!record.locationApiKey?.trim()) {
    return 'LOCATION_API_KEY_MISSING';
  }

  return undefined;
}

async function applyFieldSyncTransportResult(
  repositories: FieldSyncRunnerRepositories,
  session: CaptureSession,
  result: FieldSyncTransportResult,
  now: string,
): Promise<FieldSyncRunResult> {
  const outcome = createFieldSyncTransportOutcome(result);
  // The transport only reports ids from successful responses, including partial saves.
  session = {
    ...session,
    ...(result.remoteEntityIds?._id ? { _id: result.remoteEntityIds._id } : {}),
    ...(result.remoteEntityIds?.id ? { meterId: result.remoteEntityIds.id } : {}),
  };

  if (outcome.queueStatus === SYNC_QUEUE_STATUSES.synced) {
    await markPendingFloorplanUploaded(repositories, session, result, now);
    await repositories.captureSessions.save({
      ...session,
      photos: markPhotosUploaded(session.photos),
      status: outcome.sessionStatus,
      updatedAt: now,
      validationErrors: [],
    });
    await repositories.syncQueue.markSynced(session.id, now);
    await deleteLocalPhotoBlobs(repositories.photoBlobs, session.photos).catch(() => undefined);

    return { sessionId: session.id, status: outcome.queueStatus };
  }

  if (outcome.queueStatus === SYNC_QUEUE_STATUSES.conflict) {
    await repositories.captureSessions.save({
      ...session,
      status: outcome.sessionStatus,
      updatedAt: now,
      validationErrors: result.validationErrors ?? session.validationErrors,
    });
    await repositories.syncQueue.markConflict(session.id, now, outcome.errorCode);

    return { errorCode: outcome.errorCode, sessionId: session.id, status: outcome.queueStatus };
  }

  await repositories.captureSessions.save({
    ...session,
    status: outcome.sessionStatus,
    updatedAt: now,
    validationErrors: result.validationErrors ?? session.validationErrors,
  });
  await repositories.syncQueue.markNeedsReview(session.id, {
    updatedAt: now,
    errorCode: outcome.errorCode,
  });

  return { errorCode: outcome.errorCode, sessionId: session.id, status: outcome.queueStatus };
}

async function recordFieldSyncFailure(
  repositories: Pick<FieldSyncRunnerRepositories, 'syncQueue'>,
  sessionId: string,
  now: string,
  errorCode: string,
) {
  await repositories.syncQueue.markFailed(sessionId, {
    errorCode,
    updatedAt: now,
  });
}

async function markPendingFloorplanUploaded(
  repositories: Pick<FieldSyncRunnerRepositories, 'floorplanBlobs'>,
  session: CaptureSession,
  result: FieldSyncTransportResult,
  now: string,
) {
  const floorplanBlobId = readInventorizationPosition(session).floorplanBlobId;

  if (!floorplanBlobId) {
    return;
  }

  await repositories.floorplanBlobs.markUploaded(floorplanBlobId, {
    floorplanId: readRemoteFloorplanId(result.remoteEntityIds),
    uploadedAt: now,
  });
}

function createFieldSyncTransportOutcome(result: FieldSyncTransportResult) {
  if (result.status === FIELD_SYNC_TRANSPORT_STATUSES.accepted) {
    return {
      queueStatus: SYNC_QUEUE_STATUSES.synced,
      sessionStatus: CAPTURE_SESSION_STATUSES.synced,
    };
  }

  if (result.status === FIELD_SYNC_TRANSPORT_STATUSES.conflict) {
    return {
      errorCode: result.errorCode ?? 'CONFLICT',
      queueStatus: SYNC_QUEUE_STATUSES.conflict,
      sessionStatus: CAPTURE_SESSION_STATUSES.needsReview,
    };
  }

  const errorCode =
    result.errorCode ?? (result.status === FIELD_SYNC_TRANSPORT_STATUSES.partial ? 'PARTIAL_SYNC' : 'SYNC_REJECTED');

  return {
    errorCode,
    queueStatus: SYNC_QUEUE_STATUSES.needsReview,
    sessionStatus: CAPTURE_SESSION_STATUSES.needsReview,
  };
}

function readRemoteFloorplanId(remoteEntityIds: FieldSyncTransportResult['remoteEntityIds']) {
  return remoteEntityIds?.floorplanId ?? remoteEntityIds?.inventoryLocationBuildingFloorplanId;
}

function markPhotosUploaded(photos: CaptureSession['photos']) {
  return photos.map(photo => ({
    ...photo,
    uploadedRemoteId: photo.uploadedRemoteId ?? 'synced',
  }));
}
